import React, { useEffect, useRef, useState } from 'react';

import { Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '@/store';

const LandingPage: React.FC = () => {
	const isLoggedIn = useAuthStore((s) => s.access_token);
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry) {
					setIsScrolled(!entry.isIntersecting);
				}
			},
			{ threshold: 0 }
		);
		if (sentinelRef.current) observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, []);

	return (
		<div className="min-h-screen font-sans text-slate-800">
			<div
				ref={sentinelRef}
				className="absolute top-0 left-0 z-[-1] h-1 w-full bg-transparent"
			/>

			<nav
				className={`fixed top-0 z-50 w-full px-4 transition-all duration-300 ease-in-out ${isScrolled ? 'bg-white py-2 shadow-md' : 'bg-transparent py-4'}`}
			>
				<div className="container mx-auto">
					<div className="flex items-center">
						<div className="flex w-1/2 items-center">
							<Link to="/">
								<img
									src="/logo/circle-logo.webp"
									alt="CIRCLE"
									className="h-8 md:h-10"
								/>
							</Link>
						</div>

						<div className="flex w-1/2 justify-end">
							<div className="flex w-full items-center justify-end">
								<div className="mr-8 hidden items-center md:flex">
									{isLoggedIn ? (
										<Link
											to="/home"
											className="font-semibold text-orange-600 hover:text-orange-700"
										>
											Home
										</Link>
									) : (
										<ul className="m-0 flex list-none flex-row items-center p-0 text-sm font-bold">
											<li className="mr-4 border-r border-black pr-4">
												<Link
													to="/register"
													className="text-orange-600 no-underline hover:text-orange-800"
												>
													Daftar
												</Link>
											</li>
											<li>
												<Link
													to="/login"
													className="text-orange-600 no-underline hover:text-orange-800"
												>
													Login
												</Link>
											</li>
										</ul>
									)}
								</div>
								<div className="shrink-0">
									<img
										src="/logo/satu-indonesia.webp"
										alt="Satu Indonesia"
										className="h-8 md:h-10"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</nav>

			<main
				className="relative flex min-h-[calc(100vh-20%)] w-full items-center bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage: "url('/image/banner_customer_circle.webp')"
				}}
			>
				<div className="container mx-auto px-4">
					<div className="flex flex-wrap">
						<div className="w-full pt-20 md:w-4/12 md:pt-0 lg:ml-12">
							<h3 className="mb-2 text-2xl font-bold text-slate-800 md:text-3xl">
								Tentang CIRCLE
							</h3>
							<div className="mb-6 h-0.5 w-16 bg-slate-800"></div>

							<p className="mb-8 leading-relaxed font-medium text-slate-700">
								CIRCLE adalah platform vendor registrasi yang dapat mempermudah proses
								onboarding, verifikasi, dan pengelolaan vendor secara cepat dan
								terintegrasi.
							</p>

							<div className="flex flex-col items-center gap-3 text-center md:items-start md:text-left">
								<Link
									to={'/register'}
									className="w-64 rounded-full bg-[#00a8ff] px-4 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-sky-600"
								>
									Daftar Sebagai Vendor
								</Link>
								<Link
									to={'/home'}
									className="w-64 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-[#00a8ff] shadow-sm transition-all hover:bg-gray-50"
								>
									Ke CIRCLE Procurement
								</Link>
							</div>
						</div>
					</div>
				</div>
			</main>

			<footer
				className="relative flex min-h-[400px] items-center bg-orange-400 bg-cover bg-center"
				style={{
					backgroundImage: "url('/image/footer.webp')"
				}}
			>
				<div className="container mx-auto px-4">
					<div className="flex flex-wrap">
						<div className="w-full text-slate-700 md:w-3/12">
							<div className="mb-4 inline-block rounded-full bg-white px-4 py-2 shadow-sm">
								<img src="/logo/circle-logo.webp" alt="CIRCLE" className="h-6" />
							</div>
							<address className="text-sm leading-relaxed font-medium text-white not-italic">
								Jl.Gaya Motor Raya No.8 Sunter II - Jakarta Utara
								<br />
								Jakarta 14330 Indonesia
							</address>
						</div>
					</div>
				</div>
			</footer>

			<a
				href="mailto:circle.astra.co.id"
				className="group fixed right-6 bottom-6 z-50"
			>
				<div className="flex cursor-pointer items-center gap-2 rounded-full border border-gray-100 bg-white py-2.5 pr-5 pl-4 shadow-lg transition-all hover:shadow-xl">
					<Mail className="h-5 w-5 text-[#FE6700]" />
					<span className="text-sm font-bold text-slate-800">Hubungi Kami</span>
				</div>
			</a>
		</div>
	);
};

export default LandingPage;
